import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Button, Modal, Form, Input, Select, Switch, Tag, message,
  Space, InputNumber, Row, Col, Progress, Typography, Divider, Badge,
  Alert, Popconfirm, Drawer, Collapse,
} from 'antd';
import {
  UserAddOutlined, CheckOutlined, CloseOutlined, ClockCircleOutlined,
  MenuOutlined, LockOutlined,
} from '@ant-design/icons';
import { authAPI } from '../services/api';

const { Text } = Typography;

// All available menu routes grouped — mirrors App.js sidebar
const MENU_GROUPS = [
  {
    group: 'Bangkok Real Estate',
    items: [
      { key: '/jj-dashboard', label: 'Dashboard' },
      { key: '/jj-inbox', label: 'Inbox' },
      { key: '/jj-crm', label: 'Case Management CRM' },
      { key: '/jj-properties', label: '房屋管理 Properties' },
      { key: '/jj-keywords', label: 'Property Keywords' },
      { key: '/jj-renewals', label: '續約管理 Renewals' },
      { key: '/house-ai', label: 'HOUSE AI' },
      { key: '/house-ai-rental', label: 'Property Search' },
      { key: '/office-search', label: 'Office Rental 辦公室搜尋' },
      { key: '/home-search', label: 'Home Rental 住家搜尋' },
      { key: '/jj-hotels', label: 'Hotel Search 飯店搜尋' },
    ],
  },
  {
    group: 'Marketing',
    items: [
      { key: '/scenarios', label: '行銷情境 Scenarios' },
      { key: '/marketing-sites', label: '行銷網站 Marketing Sites' },
      { key: '/blog', label: '部落格 Blog' },
      { key: '/landing-pages', label: 'Landing Pages' },
      { key: '/marketing-scheduler', label: '行銷排程 Scheduler' },
      { key: '/video-transcribe', label: '影片分析 Video Transcribe' },
      { key: '/wav2lip', label: 'AI Video (Wav2Lip)' },
      { key: '/module-map', label: 'Module Map' },
    ],
  },
  {
    group: 'Advertising',
    items: [
      { key: '/facebook', label: 'Facebook Hub' },
      { key: '/fb-ad-creator', label: 'FB Ad Creator' },
      { key: '/google-ads', label: 'Google Ads' },
      { key: '/marketing-tools', label: '行銷工具設定' },
    ],
  },
  {
    group: 'Customer Engagement',
    items: [
      { key: '/contacts', label: '聯絡人管理 Contacts' },
      { key: '/email-campaigns', label: '郵件行銷 Email Campaigns' },
      { key: '/email-marketing', label: 'EMBot' },
      { key: '/line', label: 'LINE Bot' },
      { key: '/chatbot', label: 'AI Chatbot' },
      { key: '/mira', label: 'Mira AI' },
      { key: '/funnel', label: '行銷漏斗 Funnel' },
    ],
  },
  {
    group: 'AI Tools',
    items: [
      { key: '/quotation', label: 'AI Quotation' },
      { key: '/presentation', label: 'AI Presentation' },
      { key: '/ai-sales', label: 'AI Sales Competition' },
      { key: '/ai-sales/proposals', label: 'Bot Proposals' },
      { key: '/alayaess-bots', label: 'AlayaEss Bots' },
      { key: '/alayaess-bindings', label: 'AlayaEss Bindings' },
      { key: '/aeo', label: 'AI SEO 功能總覽' },
      { key: '/aeo-scanner', label: 'AEO Scanner' },
      { key: '/aeo-fix', label: 'AI Auto-Fix' },
      { key: '/citation-monitor', label: 'AI Citation Monitor' },
      { key: '/coupon-request', label: '申請優惠券 Coupon' },
      { key: '/research/trends', label: 'Trends' },
      { key: '/research/reports', label: 'Reports' },
      { key: '/research/settings', label: 'Source Settings' },
    ],
  },
  {
    group: 'Business',
    items: [
      { key: '/products', label: '產品管理 Products' },
      { key: '/loi', label: '意向書管理 LOI' },
      { key: '/loi/manage', label: 'LOI Import' },
      { key: '/tender', label: '招標管理 Tender' },
      { key: '/billing', label: '帳務中心 Billing' },
    ],
  },
  {
    group: 'Events',
    items: [
      { key: '/events', label: '活動報名 Events' },
      { key: '/seminar', label: '論壇報名 Seminar' },
      { key: '/email-compose', label: '信件發送 Email' },
    ],
  },
  {
    group: 'Partners',
    items: [
      { key: '/partner-manage', label: 'Partner Portal' },
      { key: '/partnership-manage', label: 'Partnership' },
    ],
  },
  {
    group: 'Analytics',
    items: [
      { key: '/analytics', label: '訪客分析 Analytics' },
      { key: '/customers', label: '客戶管理 Customers' },
      { key: '/login-analytics', label: '登入分析 Login Analytics' },
    ],
  },
  {
    group: 'Ulink 銷售',
    items: [
      { key: '/ulink-sales', label: '引流 + LINE 推播' },
      { key: '/ulink-line-inbox', label: 'Mira LINE 收件匣' },
      { key: '/cdp', label: 'CDP 客戶中台' },
      { key: '/sales-flow', label: '銷售流程圖' },
    ],
  },
];

function UserQuotaUsage({ user, kind }) {
  if (!user) return null;
  const used = user[`${kind}_tokens_used`] || 0;
  const limit = user[`${kind}_token_limit`];
  if (!limit) {
    return <Text type="secondary" style={{ fontSize: 12 }}>已用：{used.toLocaleString()}（無上限）</Text>;
  }
  const pct = Math.min(100, Math.round((used / limit) * 100));
  const status = pct >= 100 ? 'exception' : pct >= 80 ? 'active' : 'normal';
  const color = pct >= 100 ? '#dc2626' : pct >= 80 ? '#d97706' : '#16a34a';
  return (
    <div style={{ marginTop: -8, marginBottom: 12 }}>
      <Progress percent={pct} size="small" status={status} strokeColor={color} showInfo={false} />
      <Text type="secondary" style={{ fontSize: 12 }}>
        {used.toLocaleString()} / {limit.toLocaleString()} ({pct}%)
        {pct >= 100 ? ' — 已封頂' : pct >= 80 ? ' — 接近上限' : ''}
      </Text>
    </div>
  );
}

// Menu permission drawer
function MenuConfigDrawer({ user, onClose, onSaved }) {
  const allKeys = MENU_GROUPS.flatMap(g => g.items.map(i => i.key));
  // menu_config === null → all allowed; otherwise only keys explicitly `true` are enabled
  // (App.js filters with cfg[key] === true, so unlisted keys are hidden).
  const init = () => {
    const cfg = user?.menu_config ?? null;
    const result = {};
    allKeys.forEach(k => { result[k] = cfg === null ? true : cfg[k] === true; });
    return result;
  };

  const [config, setConfig] = useState(init);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const cfg = user?.menu_config ?? null;
    const result = {};
    allKeys.forEach(k => { result[k] = cfg === null ? true : cfg[k] === true; });
    setConfig(result);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleAll = (val) => {
    const next = {};
    allKeys.forEach(k => { next[k] = val; });
    setConfig(next);
  };

  const toggleGroup = (group, val) => {
    const keys = group.items.map(i => i.key);
    setConfig(prev => {
      const next = { ...prev };
      keys.forEach(k => { next[k] = val; });
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // If everything is true → save null (all allowed)
      const allOn = allKeys.every(k => config[k]);
      await authAPI.updateMenuConfig(user.id, allOn ? null : config);
      message.success('功能權限已儲存');
      onSaved();
      onClose();
    } catch (e) {
      message.error('儲存失敗: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const enabledCount = allKeys.filter(k => config[k]).length;

  return (
    <Drawer
      title={
        <Space>
          <LockOutlined />
          <span>功能選單權限 — {user?.display_name || user?.username}</span>
          <Tag color={enabledCount === allKeys.length ? 'green' : 'orange'}>
            {enabledCount}/{allKeys.length} 開啟
          </Tag>
        </Space>
      }
      open={!!user}
      onClose={onClose}
      width={480}
      footer={
        <Space style={{ float: 'right' }}>
          <Button onClick={onClose}>取消</Button>
          <Button onClick={() => toggleAll(false)}>全部關閉</Button>
          <Button onClick={() => toggleAll(true)}>全部開啟</Button>
          <Button type="primary" loading={saving} onClick={handleSave}>儲存</Button>
        </Space>
      }
    >
      <Alert
        message="管理員帳號不受此設定影響，永遠可存取所有功能。"
        type="info" showIcon style={{ marginBottom: 16 }}
      />
      <Collapse
        defaultActiveKey={MENU_GROUPS.map((_, i) => String(i))}
        items={MENU_GROUPS.map((group, gi) => {
          const groupKeys = group.items.map(i => i.key);
          const groupOn = groupKeys.every(k => config[k]);
          const groupPartial = !groupOn && groupKeys.some(k => config[k]);
          return {
            key: String(gi),
            label: (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontWeight: 600 }}>{group.group}</span>
                <Switch
                  size="small"
                  checked={groupOn}
                  style={groupPartial ? { backgroundColor: '#fa8c16' } : {}}
                  onChange={v => { toggleGroup(group, v); }}
                  onClick={(_, e) => e.stopPropagation()}
                />
              </div>
            ),
            children: (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {group.items.map(item => (
                  <div key={item.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Text style={{ fontSize: 13 }}>{item.label}</Text>
                    <Switch
                      size="small"
                      checked={!!config[item.key]}
                      onChange={v => setConfig(prev => ({ ...prev, [item.key]: v }))}
                    />
                  </div>
                ))}
              </div>
            ),
          };
        })}
      />
    </Drawer>
  );
}

function UserManagementPage() {
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [menuUser, setMenuUser] = useState(null);
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [approvingId, setApprovingId] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const [allRes, pendingRes] = await Promise.all([
        authAPI.getUsers(),
        authAPI.getPendingUsers(),
      ]);
      setUsers(allRes.data);
      setPendingUsers(pendingRes.data || []);
    } catch (error) {
      message.error('載入用戶列表失敗: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const openCreateModal = () => {
    setEditingUser(null);
    form.resetFields();
    form.setFieldsValue({ role: 'manager', is_active: true });
    setModalVisible(true);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    form.resetFields();
    form.setFieldsValue({
      display_name: user.display_name,
      email: user.email || '',
      company_name: user.company_name || '',
      role: user.role,
      is_active: user.is_active,
      password: '',
      public_token_limit: user.public_token_limit ?? null,
      private_token_limit: user.private_token_limit ?? null,
    });
    setModalVisible(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      if (editingUser) {
        const updateData = {
          display_name: values.display_name,
          email: values.email || undefined,
          company_name: values.company_name || undefined,
          role: values.role,
          is_active: values.is_active,
        };
        if (values.password) updateData.password = values.password;
        updateData.public_token_limit = values.public_token_limit || null;
        updateData.private_token_limit = values.private_token_limit || null;
        await authAPI.updateUser(editingUser.id, updateData);
        message.success('用戶更新成功');
      } else {
        await authAPI.createUser({
          username: values.username,
          password: values.password,
          display_name: values.display_name,
          email: values.email || undefined,
          company_name: values.company_name || undefined,
          role: values.role,
        });
        message.success('用戶建立成功');
      }
      setModalVisible(false);
      fetchUsers();
    } catch (error) {
      if (error.errorFields) return;
      message.error('操作失敗: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async (userId) => {
    setApprovingId(userId);
    try {
      await authAPI.approveUser(userId);
      message.success('已核准，該用戶現在可以登入');
      fetchUsers();
    } catch (e) {
      message.error('核准失敗: ' + e.message);
    } finally {
      setApprovingId(null);
    }
  };

  const handleReject = async (userId) => {
    setApprovingId(userId);
    try {
      await authAPI.rejectUser(userId);
      message.success('已拒絕該申請');
      fetchUsers();
    } catch (e) {
      message.error('拒絕失敗: ' + e.message);
    } finally {
      setApprovingId(null);
    }
  };

  const handleDelete = (user) => {
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除用戶「${user.display_name || user.username}」嗎？此操作無法復原。`,
      okText: '刪除', okType: 'danger', cancelText: '取消',
      onOk: async () => {
        try {
          await authAPI.deleteUser(user.id);
          message.success('用戶已刪除');
          fetchUsers();
        } catch (e) {
          message.error('刪除失敗: ' + e.message);
        }
      },
    });
  };

  const allRouteCount = MENU_GROUPS.reduce((s, g) => s + g.items.length, 0);

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 55 },
    { title: '帳號', dataIndex: 'username', key: 'username', width: 120 },
    { title: '顯示名稱', dataIndex: 'display_name', key: 'display_name' },
    {
      title: '角色', dataIndex: 'role', key: 'role', width: 90,
      render: (role) => {
        const map = { admin: ['red', '管理員'], manager: ['blue', '管理者'], customer: ['green', '客戶'] };
        const [color, label] = map[role] || ['default', role];
        return <Tag color={color}>{label}</Tag>;
      },
    },
    { title: '公司', dataIndex: 'company_name', key: 'company_name', render: v => v || '-' },
    { title: 'Email', dataIndex: 'email', key: 'email', render: v => v || '-' },
    {
      title: '狀態', key: 'status', width: 90,
      render: (_, r) => (
        <Space direction="vertical" size={2}>
          <Tag color={r.is_active ? 'green' : 'red'}>{r.is_active ? '啟用' : '停用'}</Tag>
          {!r.is_approved && <Tag color="orange" icon={<ClockCircleOutlined />}>待審核</Tag>}
        </Space>
      ),
    },
    {
      title: '功能權限', key: 'menu', width: 110,
      render: (_, r) => {
        if (r.role === 'admin') return <Tag color="red">全部（管理員）</Tag>;
        const cfg = r.menu_config;
        if (!cfg) return <Tag color="green">全部開啟</Tag>;
        const on = Object.values(cfg).filter(Boolean).length;
        return (
          <Tag color={on === allRouteCount ? 'green' : 'orange'}>
            {on}/{allRouteCount} 項
          </Tag>
        );
      },
    },
    { title: '建立時間', dataIndex: 'created_at', key: 'created_at', width: 140, render: v => v ? new Date(v).toLocaleDateString('zh-TW') : '-' },
    {
      title: '操作', key: 'actions', width: 180,
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" onClick={() => openEditModal(record)}>編輯</Button>
          <Button
            type="link" size="small" icon={<MenuOutlined />}
            onClick={() => setMenuUser(record)}
            disabled={record.role === 'admin'}
          >
            功能權限
          </Button>
          <Button type="link" danger size="small" onClick={() => handleDelete(record)}>刪除</Button>
        </Space>
      ),
    },
  ];

  const pendingColumns = [
    { title: '帳號', dataIndex: 'username', key: 'username', width: 140 },
    { title: '姓名', dataIndex: 'display_name', key: 'display_name' },
    { title: '公司', dataIndex: 'company_name', key: 'company_name', render: v => v || '-' },
    { title: 'Email', dataIndex: 'email', key: 'email', render: v => v || '-' },
    { title: '申請時間', dataIndex: 'created_at', key: 'created_at', width: 160, render: v => v ? new Date(v).toLocaleString('zh-TW') : '-' },
    {
      title: '操作', key: 'actions', width: 160,
      render: (_, record) => (
        <Space>
          <Button type="primary" size="small" icon={<CheckOutlined />} loading={approvingId === record.id} onClick={() => handleApprove(record.id)}>核准</Button>
          <Popconfirm title="確定拒絕此申請？" onConfirm={() => handleReject(record.id)}>
            <Button danger size="small" icon={<CloseOutlined />} loading={approvingId === record.id}>拒絕</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>帳號管理</h2>
        <Button type="primary" icon={<UserAddOutlined />} onClick={openCreateModal}>新增用戶</Button>
      </div>

      {pendingUsers.length > 0 && (
        <Alert
          type="warning" showIcon icon={<ClockCircleOutlined />}
          style={{ marginBottom: 16 }}
          message={<span><Badge count={pendingUsers.length} style={{ marginRight: 8 }} />筆新用戶申請待審核</span>}
          description={
            <Table columns={pendingColumns} dataSource={pendingUsers} rowKey="id" size="small" pagination={false} style={{ marginTop: 8 }} />
          }
        />
      )}

      <Table columns={columns} dataSource={users} rowKey="id" loading={loading} pagination={false} scroll={{ x: 1000 }} />

      {/* Edit / Create modal */}
      <Modal
        title={editingUser ? '編輯用戶' : '新增用戶'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        confirmLoading={submitting}
        okText={editingUser ? '更新' : '建立'}
        cancelText="取消"
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          {!editingUser && (
            <Form.Item name="username" label="帳號" rules={[{ required: true, message: '請輸入帳號' }]}>
              <Input placeholder="輸入帳號" />
            </Form.Item>
          )}
          <Form.Item name="password" label={editingUser ? '重設密碼（留空則不變更）' : '密碼'}
            rules={editingUser ? [] : [{ required: true, message: '請輸入密碼' }]}>
            <Input.Password placeholder={editingUser ? '留空則不變更' : '輸入密碼'} />
          </Form.Item>
          <Form.Item name="display_name" label="顯示名稱" rules={[{ required: true, message: '請輸入顯示名稱' }]}>
            <Input placeholder="輸入顯示名稱" />
          </Form.Item>
          <Form.Item name="email" label="Email">
            <Input placeholder="Email（選填）" />
          </Form.Item>
          <Form.Item name="company_name" label="公司名稱">
            <Input placeholder="公司名稱（選填）" />
          </Form.Item>
          <Form.Item name="role" label="角色" rules={[{ required: true, message: '請選擇角色' }]}>
            <Select>
              <Select.Option value="admin">管理員 (admin)</Select.Option>
              <Select.Option value="manager">管理者 (manager)</Select.Option>
              <Select.Option value="customer">客戶 (customer)</Select.Option>
            </Select>
          </Form.Item>
          {editingUser && (
            <Form.Item name="is_active" label="啟用狀態" valuePropName="checked">
              <Switch checkedChildren="啟用" unCheckedChildren="停用" />
            </Form.Item>
          )}
          {editingUser && (
            <>
              <Divider orientation="left" style={{ marginTop: 8 }}>
                <span style={{ fontSize: 13, color: '#475569' }}>AI Token 配額（累計上限，永不歸零）</span>
              </Divider>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="public_token_limit"
                    label={<span>Public 模型上限 <Text type="secondary" style={{ fontSize: 12 }}>(Claude + OpenAI)</Text></span>}
                    tooltip="留空 = 不限。達 80% log 警示，達 100% 硬擋。">
                    <InputNumber min={0} step={1000} placeholder="無上限" style={{ width: '100%' }} addonAfter="tok" />
                  </Form.Item>
                  <UserQuotaUsage user={editingUser} kind="public" />
                </Col>
                <Col span={12}>
                  <Form.Item name="private_token_limit"
                    label={<span>Private 模型上限 <Text type="secondary" style={{ fontSize: 12 }}>(OLLAMA + 本地)</Text></span>}
                    tooltip="留空 = 不限。">
                    <InputNumber min={0} step={1000} placeholder="無上限" style={{ width: '100%' }} addonAfter="tok" />
                  </Form.Item>
                  <UserQuotaUsage user={editingUser} kind="private" />
                </Col>
              </Row>
            </>
          )}
        </Form>
      </Modal>

      {/* Menu permission drawer */}
      <MenuConfigDrawer
        user={menuUser}
        onClose={() => setMenuUser(null)}
        onSaved={fetchUsers}
      />
    </div>
  );
}

export default UserManagementPage;
